import React from "react";
import MaterialIcon from "../../../../../../components/material-icon";
import { colorPalette } from "../../../../../../utils/theme";
import { CardContainer, PackageHeader, TitleContainer, PackageIcon, PackageTitle, PackageSubtitle, PriceInfo, PriceAmount, PriceText, TooltipIcon, PriceInfoDetail, FeatureList, FeatureItem, IconWrapper, FeatureItemTitle, ViewMoreLink } from "./package-card.styles";
import LogoIconWhite from '../../../../../../assets/icons/logo-icon-white.png';
import LogoIconDark from '../../../../../../assets/icons/logo-icon-dark.png';
import { useState } from "react";
import PricingModal from "../../../../../../modals/pricing-modal/pricing-modal";
import PackageBenefitsModal from "../../../../../../modals/package-benefits-modal/package-benefits-modal";
import { useTranslation } from "react-i18next";

interface PackageCardProps {
    title: string;
    price: number;
    features: string[];
    isPremium: boolean;
}

const PackageCard: React.FC<PackageCardProps> = ({
    title,
    price,
    features,
    isPremium,
}) => {
    const { t } = useTranslation();
    
    // States
    const [isPricingModalVisible, setIsPricingModalVisible] = useState(false);
    const [isPackageBenefitsVisible, setIsPackageBenefitsModalVisible] = useState(false);

    return (
        <>
            <CardContainer>
                <PackageHeader premium={isPremium}>
                    <TitleContainer>
                        <PackageIcon src={isPremium ? LogoIconWhite : LogoIconDark} alt="logo" />
                        <PackageTitle>{title}</PackageTitle>
                    </TitleContainer>
                    <PackageSubtitle premium={isPremium}>
                        {isPremium ? (
                            t('package_card.premium_description').split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    {index === 0 && <br />}
                                </React.Fragment>
                            ))
                        ) : (
                            t('package_card.basic_description').split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    {index === 0 && <br />}
                                </React.Fragment>
                            ))
                        )}
                    </PackageSubtitle>
                    <PriceInfo>
                        <PriceAmount>{price}%</PriceAmount> <PriceText>{t('package_card.price_text')}</PriceText>
                        <TooltipIcon onClick={() => setIsPricingModalVisible(true)}>
                            <MaterialIcon icon="info" color={isPremium ? colorPalette.blue600 : colorPalette.blue200} />
                        </TooltipIcon>
                    </PriceInfo>
                    <PriceInfoDetail premium={isPremium}>{t('package_card.minimum_fee')}</PriceInfoDetail>
                    <PriceInfoDetail premium={isPremium}>
                        {t('package_card.additional_fees').startsWith('one-time') ? 'plus ' : 'zzgl. '}<u style={{ cursor: "pointer" }} onClick={() => setIsPricingModalVisible(true)}>{t('package_card.additional_fees')}</u> • {t('package_card.annual_billing')}
                    </PriceInfoDetail>
                </PackageHeader>
                <FeatureList>
                    {features.map((feature, index) => (
                        <FeatureItem key={index}>
                            <div className="d-flex align-items-center gap-1">
                                <IconWrapper premium={isPremium}>
                                    <MaterialIcon icon="check" size={20} color={colorPalette.white} />
                                </IconWrapper>
                                <FeatureItemTitle>{feature}</FeatureItemTitle>
                            </div>
                        </FeatureItem>
                    ))}
                </FeatureList>
                <ViewMoreLink onClick={() => setIsPackageBenefitsModalVisible(true)}>
                    {t('package_card.view_all_services')}
                </ViewMoreLink>
            </CardContainer>

            <PricingModal
                isVisible={isPricingModalVisible}
                isPremium={isPremium}
                onClose={() => setIsPricingModalVisible(false)}
            />

            <PackageBenefitsModal
                isVisible={isPackageBenefitsVisible}
                onClose={() => setIsPackageBenefitsModalVisible(false)}
            />
        </>
    );
};

export default PackageCard;
